/**
 * Notifications API — the durable inbox (bell / unread / history) + per-user
 * preferences. Backed by the eastview-os LOS `app/routers/notifications.py`,
 * which is LIVE now (rows fill in once the listener's dispatcher runs).
 *
 * Two layers (per frontend_documentation/notifications-and-presence.md §13):
 *   - INBOX  = backend-decided, NOT preference-gated, one row per activity.
 *   - PUSH   = preference-governed (live / email / Teams) — see preferences.
 *
 * All calls go through `dsHttp` (the eastview-los client) so they carry the
 * bearer JWT automatically.
 */
import { dsHttp } from "../lib/digitalSizerHttp";

export const notificationsApi = {
  // GET /notifications — filterable inbox feed (newest first).
  // params: { unread, type (string | string[]), dealId, severity, limit, before_id }
  list: (params = {}) => {
    const { type, ...rest } = params;
    // `type` is a repeatable query param on the backend (Query(None) list).
    const search = new URLSearchParams();
    Object.entries(rest).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== "") search.append(k, v);
    });
    if (Array.isArray(type)) type.forEach((t) => t && search.append("type", t));
    else if (type) search.append("type", type);
    const qs = search.toString();
    return dsHttp
      .get(`/notifications${qs ? `?${qs}` : ""}`)
      .then((r) => r.data);
  },

  // GET /notifications/unread_count → { unread: number }
  unreadCount: () =>
    dsHttp.get("/notifications/unread_count").then((r) => r.data),

  // POST /notifications/{id}/read → { updated: boolean }
  markRead: (notificationId) =>
    dsHttp
      .post(`/notifications/${notificationId}/read`)
      .then((r) => r.data),

  // POST /notifications/read_all → { updated: number }
  markAllRead: () =>
    dsHttp.post("/notifications/read_all").then((r) => r.data),

  // GET /notifications/preferences → { preferences, severities, defaultTypeChannels }
  getPreferences: () =>
    dsHttp.get("/notifications/preferences").then((r) => r.data),

  // PUT /notifications/preferences → { preferences }
  // body: { channels, severityFloor, quietHours, activeWindowMinutes, subscriptions, muteDeals }
  setPreferences: (body) =>
    dsHttp.put("/notifications/preferences", body).then((r) => r.data),
};

export default notificationsApi;
